import { useEffect, useState } from 'react'
import { Check, Copy, AlertTriangle } from 'lucide-react'
import { CalcResult, RackMeta, DcMeta, rackLabel, CABLE_LIMITS, HistoryEntry } from '../../hooks/useCalculatorLogic'

export function SummaryPanel({
  result, primaryResult, selectedIndex, onSelectRoute, error, src, tgt, srcU, tgtU, dcCode, cable, racks, currentDc, history,
  restoreHistory
}: {
  result: CalcResult | null
  primaryResult: CalcResult | null
  selectedIndex: number
  onSelectRoute: (index: number) => void
  error: string | null
  src: RackMeta | null
  tgt: RackMeta | null
  srcU: number
  tgtU: number
  dcCode?: string
  cable: string
  racks: RackMeta[]
  currentDc?: DcMeta
  history: HistoryEntry[]
  restoreHistory: (entry: HistoryEntry) => void
}) {
  const [copied, setCopied] = useState(false)
  const [copyError, setCopyError] = useState(false)
  useEffect(() => { setCopied(false); setCopyError(false) }, [result])

  const cableMeta = CABLE_LIMITS[cable] ?? CABLE_LIMITS.copper
  const evidence = result?.length_evidence
  const capacityAudit = result?.capacity_audit ?? []
  const enforced = capacityAudit.filter(record => record.enforced).length
  const unknown = capacityAudit.filter(record => record.enabled && record.unknown).length
  const overloaded = capacityAudit.filter(record => record.overloaded).length
  const restricted = capacityAudit.filter(record => record.status === 'restricted').map(record => record.edge_id)
  const planned = capacityAudit.filter(record => record.status === 'planned').map(record => record.edge_id)
  const lengthOk = result ? result.total_length <= cableMeta.maxM : true
  const variants = primaryResult ? [primaryResult, ...(primaryResult.alternatives ?? [])] : []

  const handleCopy = () => {
    if (!result || !src || !tgt || !dcCode) return
    const lines = [
      `ЦОД: ${dcCode}`,
      `${rackLabel(src, currentDc)} U${srcU} → ${rackLabel(tgt, currentDc)} U${tgtU}`,
      `Маршрут: ${selectedIndex === 0 ? 'основной' : `альтернатива ${selectedIndex}`}`,
      `Кабель: ${cableMeta.label}`,
      `Заказная длина: ${result.total_length.toFixed(2)} м`,
      ...(evidence ? [
        `Расчётная длина с запасом: ${evidence.exact_length_m.toFixed(2)} м`,
        `Магистраль: ${evidence.pathway_m.toFixed(2)} м`,
        `От стоек до магистрали: ${evidence.entry_to_pathway_m.toFixed(2)} м`,
        `Подъём / спуск: ${evidence.vertical_rise_m.toFixed(2)} / ${evidence.vertical_drop_m.toFixed(2)} м`,
        `Боковая прокладка внутри стоек (оценка): ${evidence.estimated_in_rack_m.toFixed(2)} м`,
        `Запас: ${evidence.slack_m.toFixed(2)} м`,
      ] : []),
      ...(selectedIndex > 0 ? [`Общие сегменты с основным: ${result.shared_edge_ids?.join(', ') || 'нет'}`] : []),
      result.warning ? `! ${result.warning}` : '',
      '',
      ...result.route.map((p, i) => `${i + 1}. ${p.description}  [${p.x.toFixed(1)}, ${p.y.toFixed(1)}, ${p.z.toFixed(1)}]`),
    ].filter(Boolean).join('\n')
    navigator.clipboard.writeText(lines).then(() => {
      setCopyError(false)
      setCopied(true); setTimeout(() => setCopied(false), 2000)
    }).catch(() => setCopyError(true))
  }

  return (
    <div className="calculator-summary shrink-0">
      {error && (
        <div role="alert" className="m-3 text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">
          {error}
        </div>
      )}

      {result && src && tgt && (
        <>
          <fieldset className="calculator-variants px-3 py-3 border-b border-border">
            <legend className="sr-only">Маршрут</legend>
            <div className="calculator-variant-head"><h2>Маршруты</h2><span>Расчёт, м</span><span>Заказ, м</span></div>
            {variants.map((variant, index) => <label key={index} className="calculator-variant" data-selected={index === selectedIndex}>
              <span><input type="radio" name="route-alternative" checked={index === selectedIndex}
                aria-label={index === 0 ? 'Основной' : `Альтернатива ${index}`} onChange={() => onSelectRoute(index)} />
                <span>{index === 0 ? 'Основной' : `Альтернатива ${index}`}</span></span>
              <span>{variant.length_evidence?.exact_length_m.toFixed(2) ?? '—'}</span>
              <span>{variant.total_length.toFixed(2)}</span>
            </label>)}
            {!primaryResult?.alternatives?.length && <p className="text-xs text-text-muted mt-1.5">Других допустимых путей между точками врезки нет.</p>}
          </fieldset>
          <div className="px-3 py-3 border-b border-border">
            <div className="text-xs text-text-secondary mb-1">{evidence?.standard_sku ? 'Стандартная длина (SKU)' : 'Заказная длина'}</div>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-baseline gap-1.5">
                <span className={`text-xl font-mono font-semibold ${lengthOk ? 'text-text-primary' : 'text-status-critical'}`}>
                  {result.total_length.toFixed(2)}
                </span>
                <span className="text-text-muted text-xs font-mono">м</span>
              </div>
              <button
                type="button"
                onClick={handleCopy}
                aria-label={copied ? 'Расчёт скопирован' : 'Скопировать расчёт'}
                title={copied ? 'Расчёт скопирован' : 'Скопировать расчёт'}
                className="w-8 h-8 flex items-center justify-center text-text-muted hover:text-text-primary border border-border rounded transition-colors shrink-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
            {evidence && <p className="text-xs text-text-secondary">Округление до заказа: +{Math.max(0, result.total_length - evidence.exact_length_m).toFixed(2)} м</p>}
            <div className="text-xs text-text-secondary mt-2">{cableMeta.label} · предел {cableMeta.maxM} м</div>
            {copyError && <p role="alert" className="text-xs text-status-warning mt-2">Не удалось скопировать расчёт.</p>}
          </div>

          {evidence && <div className="px-3 py-3 border-b border-border">
            <h2 className="text-xs font-semibold text-text-primary mb-2">Из чего складывается длина</h2>
            <dl className="calculator-evidence">
              {[
                ['По магистрали', evidence.pathway_m],
                ['От стоек до магистрали', evidence.entry_to_pathway_m],
                ['Подъём в стойке A', evidence.vertical_rise_m],
                ['Спуск в стойке B', evidence.vertical_drop_m],
                ['Боковая прокладка (оценка)', evidence.estimated_in_rack_m],
                ['Запас на обоих концах', evidence.slack_m],
              ].map(([label, value], index) => <div key={label} className="calculator-evidence-row">
                <dt>{label}</dt>
                <dd>{Number(value).toFixed(2)} м</dd>
                <div className="calculator-evidence-bar" aria-hidden="true"><span data-part={index}
                  style={{ width: `${Math.min(100, Number(value) / Math.max(evidence.exact_length_m, 0.01) * 100)}%` }} /></div>
              </div>)}
              <div className="calculator-evidence-total"><dt>Расчётная с запасом</dt><dd>{evidence.exact_length_m.toFixed(2)} м</dd></div>
            </dl>
            {evidence.in_rack_estimated && <p className="text-xs text-text-muted mt-2">Внутристоечная часть оценочная, с точностью до U.</p>}
          </div>}

          <div className="px-3 py-3 border-b border-border text-xs">
            <details>
            <summary className="font-semibold text-text-primary cursor-pointer mb-1.5">Ограничения маршрута</summary>
            <p className="text-text-secondary">Недоступные сегменты и несовместимые типы лотков исключены.</p>
            <p className="text-text-secondary mt-1.5">{enforced ? `Вместимость: блокировка на ${enforced} сегм.` : 'Вместимость: без блокировки.'}</p>
            {unknown > 0 && <p className="text-status-warning mt-1.5">Лимит вместимости неизвестен: {unknown} сегм.</p>}
            {overloaded > 0 && <p className="text-status-warning mt-1.5">Аудит: перегружено {overloaded} сегм. Блокировка не включена.</p>}
            {restricted.length > 0 && <p className="text-status-warning mt-1.5 break-all">Ограниченный доступ: {restricted.join(', ')}.</p>}
            {planned.length > 0 && <p className="text-status-warning mt-1.5 break-all">Планируемые сегменты: {planned.join(', ')}.</p>}
            </details>
            {unknown > 0 && <p className="text-status-warning mt-1.5">Неизвестная вместимость: {unknown} сегм.</p>}
            {overloaded > 0 && <p className="text-status-warning mt-1.5">Перегрузка: {overloaded} сегм. Только аудит.</p>}
            {(restricted.length > 0 || planned.length > 0) && <p className="text-status-warning mt-1.5">Есть ограниченные или планируемые сегменты.</p>}
            {selectedIndex > 0 && <div className="mt-2">
              {result.shared_edge_ids?.length ? <>
                <p className="text-status-warning">Общие сегменты с основным: {result.shared_edge_ids.length}. Полного разнесения нет.</p>
                <ul className="mt-1.5 space-y-1 text-text-secondary">
                  {result.shared_edge_ids.map(id => <li key={id} className="break-all"><span className="calculator-shared-swatch" />{id}</li>)}
                </ul>
              </> : <p className="text-text-secondary">Общих сегментов магистрали нет. Общие узлы и вводы стоек не исключены.</p>}
            </div>}
          </div>

          {result.warning && (
            <div className="px-3 py-2 text-[11px] text-status-warning bg-yellow-500/5 border-b border-yellow-500/15 flex gap-1.5">
              <AlertTriangle size={14} className="shrink-0" /><span>{result.warning}</span>
            </div>
          )}

          <div className="px-3 py-2 border-b border-border">
            <details>
            <summary className="text-xs text-text-secondary cursor-pointer py-1">
              Маршрут · {result.route.length} точек
            </summary>
            <div className="flex flex-col gap-0">
              {result.route.map((p, i) => (
                <div key={i} className="flex gap-2 py-1.5 border-b border-border last:border-0 relative">
                  {i < result.route.length - 1 && (
                    <div className="absolute left-[9px] top-7 bottom-0 w-px bg-border z-0" />
                  )}
                  <div className={`w-[18px] h-[18px] rounded-full border shrink-0 flex items-center justify-center text-[8px] font-bold z-10
                    ${i === 0 ? 'border-status-warning text-status-warning' :
                      i === result.route.length - 1 ? 'border-status-normal text-status-normal' :
                      'border-border text-text-muted'}`}>
                    {i + 1}
                  </div>
                  <div className="min-w-0">
                    <div className="text-[11px] text-text-primary leading-tight">{p.description}</div>
                    <div className="text-[10px] text-text-muted font-mono mt-0.5">
                      {p.x.toFixed(1)}, {p.y.toFixed(1)}, z:{p.z.toFixed(1)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            </details>
          </div>
        </>
      )}

      {!result && !error && (
        <div className="px-3 py-4 text-center text-[11px] text-text-muted">
          {racks.length === 0 && dcCode
            ? <span className="text-status-warning">В ЦОД «{dcCode}» нет опубликованных стоек</span>
            : 'Маршрут ещё не рассчитан'
          }
        </div>
      )}

      {history.length > 0 && (
        <div className="px-3 py-2 border-t border-border mt-auto">
          <h2 className="text-xs text-text-secondary mb-1.5">История расчётов</h2>
          <div className="flex flex-col gap-1">
            {history.map(h => (
              <button
                key={h.id}
                type="button"
                onClick={() => restoreHistory(h)}
                title={`${h.srcLabel} U${h.srcU} → ${h.tgtLabel} U${h.tgtU} · ${CABLE_LIMITS[h.cableType]?.label ?? h.cableType}`}
                aria-label={`${h.srcLabel} U${h.srcU} → ${h.tgtLabel} U${h.tgtU} · ${CABLE_LIMITS[h.cableType]?.label ?? h.cableType}`}
                className="calculator-history-item"
              >
                <span className={h.warning ? 'text-status-warning' : 'text-status-normal'}>
                  {h.warning ? <AlertTriangle size={14} /> : <Check size={14} />}
                </span>
                <span className="flex-1 min-w-0 text-text-secondary">
                  <span className="block font-mono break-words">{h.srcLabel} U{h.srcU} → {h.tgtLabel} U{h.tgtU}</span>
                  <small>{CABLE_LIMITS[h.cableType]?.label ?? h.cableType}</small>
                </span>
                <span className="text-text-muted shrink-0 tabular-nums">{h.length.toFixed(1)}м</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
